// client/src/components/doctors/PatientManagement/PatientMoodCheckIns.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import moodAnalyticsService from '../../../services/moodAnalyticsService';
import '../../../styles/components/doctor/PatientMoodCheckIns.css';
import pdfIcon from '../../../assets/icons/indicators/export_icon.svg';
import moodHeaderIcon from '../../../assets/icons/indicators/mood-checkin_indicator.svg';

import DoctorStatusGuard, { StatusBanner, RestrictedFeatureMessage } from '../../common/DoctorStatusGuard';
import '../../../styles/components/doctor/DoctorStatus.css';

import neurolexLogo from '../../../assets/images/Neurolex_Logo_New.png';

const PatientMoodCheckIns = () => {
  const navigate = useNavigate(); 
  const [analyticsData, setAnalyticsData] = useState(null);
  const [patientMoodHistory, setPatientMoodHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDays, setSelectedDays] = useState(7);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [user, setUser] = useState(null);
  

  // FINAL: Exact working mood URLs from Cloudinary 
  const moodSvgUrls = {
    great: 'https://res.cloudinary.com/dm7qxemrt/image/upload/v1749199687/Face_Im_Great_anfp2e.svg',
    good: 'https://res.cloudinary.com/dm7qxemrt/image/upload/v1749199687/Face_Im_good_ldcpo4.svg',
    okay: 'https://res.cloudinary.com/dm7qxemrt/image/upload/v1749199687/Face_Im_okay_tcf9cp.svg',
    struggling: 'https://res.cloudinary.com/dm7qxemrt/image/upload/v1749199687/Face_Im_struggling_regocn.svg',
    upset: 'https://res.cloudinary.com/dm7qxemrt/image/upload/v1749199687/Face_Im_upset_pqskxp.svg'
  };

  // Mood colors for consistency
  const moodColors = {
    great: '#4CAF50',
    good: '#8BC34A', 
    okay: '#FFC107',
    struggling: '#FF9800',
    upset: '#F44336'
  };

  // Load user data for StatusGuard
  const loadUserData = () => {
    try {
      const userData = localStorage.getItem('user') || sessionStorage.getItem('user');
      if (userData) {
        const userObj = JSON.parse(userData);
        setUser(userObj);
        console.log('🔒 [PatientMoodCheckIns StatusGuard] User loaded:', {
          accountStatus: userObj.accountStatus,
          verificationStatus: userObj.verificationStatus,
          role: userObj.role
        });
      }
    } catch (error) {
      console.error('❌ [PatientMoodCheckIns StatusGuard] Error loading user:', error);
    }
  };

  // ✅ UPDATED: Navigate to mood analysis page
  const handleViewMoodAnalysis = (patientEntry) => {
    console.log('🔍 Navigating to mood analysis for patient:', patientEntry);
    
    // Format patient data for the page
    const patientData = {
      patientId: patientEntry.patientId,
      patientName: patientEntry.patientName,
      patientEmail: patientEntry.patientEmail,
      totalLogs: patientEntry.totalLogs,
      registrationDate: patientEntry.registrationDate,
      averageRating: patientEntry.averageRating,
      lastMood: patientEntry.lastMood,
      sentimentSummary: patientEntry.sentimentSummary,
      date: patientEntry.date
    };
    
    // Navigate to the mood analysis page
    navigate(`/doctor/patients/${patientEntry.patientId}/mood-analysis`, {
      state: { patientData }
    });
  };

  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();
      const filteredData = getFilteredMoodHistory();
      
      // Add title
      doc.setFontSize(18);
      doc.setTextColor(40, 40, 40);
      doc.text('Patient Mood Check-in History Report', 14, 20);
      
      // Add date range info
      doc.setFontSize(11);
      doc.setTextColor(100, 100, 100);
      doc.text(`Report Generated: ${new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })}`, 14, 28);
      doc.text(`Period: Last ${selectedDays} days`, 14, 34);
      
      // Add summary statistics
      doc.setFontSize(12);
      doc.setTextColor(40, 40, 40);
      doc.text('Summary Statistics:', 14, 45);
      
      doc.setFontSize(10);
      doc.text(`Total Records: ${filteredData.length}`, 14, 52);
      doc.text(`Total Logs: ${keyMetrics.totalLogs}`, 14, 58);
      doc.text(`Average Mood Score: ${keyMetrics.averageMoodScore}`, 14, 64);
      
      // Prepare table data
      const tableData = filteredData.map(entry => [
        new Date(entry.date).toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        }),
        entry.patientName,
        entry.averageRating ? `${entry.averageRating.toFixed(1)}/5` : 'N/A',
        `${entry.totalLogs} logs`,
        entry.lastMood ? entry.lastMood.moodLabel : 'No recent mood',
        entry.sentimentSummary?.latestRiskLevel || 'N/A'
      ]);
      
      // Add table
      autoTable(doc, {
        startY: 75,
        head: [['Date', 'Patient', 'Average', 'Total Logs', 'Last Mood', 'Risk Level']],
        body: tableData,
        theme: 'striped',
        headStyles: {
          fillColor: [72, 133, 237],
          textColor: 255,
          fontSize: 10,
          fontStyle: 'bold',
          halign: 'left'
        },
        bodyStyles: {
          fontSize: 9,
          textColor: 50
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 40 },
          2: { cellWidth: 25 },
          3: { cellWidth: 25 },
          4: { cellWidth: 35 },
          5: { cellWidth: 25 }
        },
        margin: { top: 75, left: 14, right: 14 },
        didDrawPage: function(data) {
          // Add footer with page numbers
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFontSize(9);
          doc.setTextColor(128);
          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            doc.internal.pageSize.width / 2,
            doc.internal.pageSize.height - 10,
            { align: 'center' }
          );
        }
      });
      
      // Generate filename with timestamp
      const fileName = `mood-checkin-history-${new Date().toISOString().split('T')[0]}.pdf`;
      
      // Save the PDF
      doc.save(fileName);
      
      console.log('✅ PDF exported successfully:', fileName);
    } catch (error) {
      console.error('❌ Error exporting PDF:', error);
      alert('Failed to export PDF. Please try again.');
    }
  };

  // Fetch analytics data
  const fetchAnalytics = async (days = 7) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await moodAnalyticsService.getDoctorPatientMoodAnalytics(days);

      if (response.success) {
        setAnalyticsData(response.data);
      } else {
        setError('Failed to fetch analytics data');
      }
    } catch (err) {
      console.error('Error fetching mood analytics:', err);
      setError(err.response?.data?.message || 'Failed to fetch mood analytics');
    } finally {
      setLoading(false);
    }
  };

  // Fetch patient mood history data
  const fetchPatientMoodHistory = async (days = 7) => {
    try {
      const response = await moodAnalyticsService.getDoctorPatientMoodHistory(days);
      
      if (response.success) {
        setPatientMoodHistory(response.data);
      } else {
        console.error('Failed to fetch patient mood history');
      }
    } catch (err) {
      console.error('Error fetching patient mood history:', err);
    }
  };

  // Load data on component mount and when days filter changes
  useEffect(() => {
    loadUserData();
    fetchAnalytics(selectedDays);
    fetchPatientMoodHistory(selectedDays);
  }, [selectedDays]);

  // Handle days filter change
  const handleDaysChange = (days) => {
    setSelectedDays(days);
  };

  // Format mood key for display
  const formatMoodLabel = (moodKey) => {
    return moodKey.charAt(0).toUpperCase() + moodKey.slice(1);
  };

  // Get mood SVG URL
  const getMoodSvg = (moodKey) => {
    return moodSvgUrls[moodKey] || moodSvgUrls['okay'];
  };

  // Get mood color
  const getMoodColor = (moodKey) => {
    return moodColors[moodKey] || '#FFC107';
  };

  // Format day name (Mon, Tue, Wed, etc.)
  const formatDayName = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  };

  // Filter and sort patient mood history
  const getFilteredMoodHistory = () => {
    let filtered = patientMoodHistory.filter(entry => {
      if (searchTerm) {
        return entry.patientName?.toLowerCase().includes(searchTerm.toLowerCase());
      }
      return true;
    });

    // Sort data
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date_asc':
          return new Date(a.date) - new Date(b.date);
        case 'date_desc':
          return new Date(b.date) - new Date(a.date);
        case 'mood':
          return a.lastMood?.moodRating - b.lastMood?.moodRating;
        case 'average':
          return a.averageRating - b.averageRating;
        default:
          return new Date(b.date) - new Date(a.date);
      }
    });

    return filtered;
  };

  // ✅ Get risk level indicator for patients with sentiment analysis
  const getRiskLevelIndicator = (sentimentSummary) => {
    if (!sentimentSummary) return null;
    
    const riskLevel = sentimentSummary.latestRiskLevel;
    const colors = {
      POSITIVE: '#4CAF50',
      LOW: '#8BC34A',
      MILD: '#FFC107',
      MODERATE: '#FF9800',
      HIGH: '#F44336',
      CRISIS: '#D32F2F',
      UNKNOWN: '#9E9E9E'
    };

    if (riskLevel && riskLevel !== 'UNKNOWN') {
      return (
        <span 
          className="risk-indicator"
          style={{ 
            backgroundColor: colors[riskLevel],
            color: 'white',
            padding: '2px 6px',
            borderRadius: '8px',
            fontSize: '10px',
            fontWeight: 'bold',
            marginLeft: '8px'
          }}
        >
          {riskLevel}
        </span>
      );
    }
    return null;
  };

  // ✅ LOADING STATE - Simple return
  if (loading) {
    return (
      <div className="neurolex-loading-container">
        <div className="neurolex-loading-insideContainer">
          <div className="neurolex-dashboard-spinner">
            <div className="spinner-circle outer"></div>
            <div className="spinner-circle middle"></div>
            <div className="spinner-circle inner"></div>
            <div className="spinner-center">
              <img 
                src={neurolexLogo} 
                alt="NeuroLex Logo" 
                className="loading-logo"
                onError={(e) => {
                  console.warn('❌ Logo failed to load from:', neurolexLogo);
                  e.target.style.display = 'none';
                  const fallback = e.target.nextElementSibling;
                  if (fallback) {
                    fallback.style.display = 'flex';
                    fallback.style.alignItems = 'center';
                    fallback.style.justifyContent = 'center';
                  }
                }}
                onLoad={() => {
                  console.log('✅ NeuroLex logo loaded successfully:', neurolexLogo);
                }}
              />
              <div className="loading-logo-fallback" style={{ display: 'none' }}>
                😊
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // ✅ ERROR STATE
  if (error) {
    return (
      <>
        <StatusBanner user={user} />
        <DoctorStatusGuard 
          user={user} 
          requiredStatus="approved"
          fallback={
            <div className="mood-checkins-container">
              <div className="mood-checkins-header">
                <div className="header-content">
                  <div className="header-left">
                    <h1>Patient Mood Check-Ins</h1>
                    <p>Honest reflections and mood updates from those working through life's challenges.</p>
                  </div>
                  <div className="header-right">
                    <img 
                      src={moodHeaderIcon} 
                      alt="Mood Header Icon" 
                      className="header-corner-icon" 
                    />
                  </div>
                </div>
                <div className="header-divider"></div>
              </div>
              <RestrictedFeatureMessage 
                title="Mood Check-In Analytics"
                message="Access to patient mood analytics is available after your account is verified."
              />
            </div>
          }
        >
          <div className="mood-checkins-container">
            <div className="error-container">
              <h3>Error Loading Data</h3>
              <p>{error}</p>
              <button onClick={() => fetchAnalytics(selectedDays)} className="retry-button">
                Try Again
              </button>
            </div>
          </div>
        </DoctorStatusGuard>
      </>
    );
  }

  // ✅ NO DATA STATE
  if (!analyticsData) {
    return (
      <>
        <StatusBanner user={user} />
        <DoctorStatusGuard 
          user={user} 
          requiredStatus="approved"
          fallback={
            <div className="mood-checkins-container">
              <div className="mood-checkins-header">
                <div className="header-content">
                  <div className="header-left">
                    <h1>Patient Mood Check-Ins</h1>
                    <p>Honest reflections and mood updates from those working through life's challenges.</p>
                  </div>
                  <div className="header-right">
                    <img 
                      src={moodHeaderIcon} 
                      alt="Mood Header Icon" 
                      className="header-corner-icon" 
                    />
                  </div>
                </div>
                <div className="header-divider"></div>
              </div>
              <RestrictedFeatureMessage 
                title="Mood Check-In Analytics"
                message="Access to patient mood analytics is available after your account is verified."
              />
            </div>
          }
        >
          <div className="mood-checkins-container">
            <div className="no-data-container">
              <h3>No Data Available</h3>
              <p>No mood check-in data found for the selected period.</p>
            </div>
          </div>
        </DoctorStatusGuard>
      </>
    );
  }

  const { keyMetrics, dailyOverview, moodDistribution } = analyticsData;

  // ✅ MAIN RETURN - Wrapped with guard
  return (
    <>
      {/* ✅ Status Banner */}
      <StatusBanner user={user} />
      
      {/* ✅ Wrap entire page in DoctorStatusGuard */}
      <DoctorStatusGuard 
        user={user} 
        requiredStatus="approved"
        fallback={
          <div className="mood-checkins-container">
            <div className="mood-checkins-header">
              <div className="header-content">
                <div className="header-left">
                  <h1>Patient Mood Check-Ins</h1>
                  <p>Honest reflections and mood updates from those working through life's challenges — one feeling at a time.</p>
                </div>
                <div className="header-right">
                  <img 
                    src={moodHeaderIcon} 
                    alt="Mood Header Icon" 
                    className="header-corner-icon" 
                  />
                </div>
              </div>
              <div className="header-divider"></div>
            </div>
            <RestrictedFeatureMessage 
              title="Mood Check-In Analytics"
              message="Access to patient mood analytics is available after your account is verified."
            />
          </div>
        }
      >
        <div className="mood-checkins-container">
          {/* Updated Header */}
          <div className="mood-checkins-header">
            <div className="header-content">
              <div className="header-left">
                <h1>Patient Mood Check-Ins</h1>
                <p>Honest reflections and mood updates from those working through life's challenges — one feeling at a time.</p>
              </div>
              <div className="header-right">
                <img 
                  src={moodHeaderIcon} 
                  alt="Mood Header Icon" 
                  className="header-corner-icon" 
                />
              </div>
            </div>
            <div className="header-divider"></div>
          </div>

          {/* Weekly Insights Header */}
          <div className="weekly-insights-header">
            <h2>This Week's Mood Summary</h2>
          </div>

          {/* Key Metrics */}
          <div className="key-metrics-section">
            <div className="metrics-grid">
              <div className="doc-mood-checkin-metric-card">
                <h3>Total Logs</h3>
                <div className="metric-card-inside">
                  <div className="metric-value">{keyMetrics.totalLogs}</div>
                  <div className="metric-label">This Week</div>
                </div>
              </div>
              <div className="doc-mood-checkin-metric-card">
                <h3>Average Logs per Day</h3>
                <div className="metric-card-inside">
                  <div className="metric-value">{keyMetrics.averageLogsPerDay}</div>
                  <div className="metric-label">Check-in logs / day</div>
                </div>
              </div>
              <div className="doc-mood-checkin-metric-card">
                <h3>Average Mood Score</h3>
                <div className="metric-card-inside">
                  <div className="metric-value">{keyMetrics.averageMoodScore}</div>
                  <div className="metric-label">out of {keyMetrics.totalLogs}</div>
                </div>
              </div>
              <div className="doc-mood-checkin-metric-card">
                <h3>Top Emotional Trends</h3>
                <div className="emotional-trends">
                  {keyMetrics.topEmotionalTrends.map((trend, index) => (
                    <div key={index} className="trend-item">
                      <div className="emotional-trends-conainer-inside-emoji">
                        <img 
                          src={getMoodSvg(trend.mood.toLowerCase())} 
                          alt={trend.mood}
                          className="trend-svg"
                        />
                      </div>
                      <div className="emotional-trends-conainer-inside">
                        <span className="trend-label">{trend.mood}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="header-divider"></div>

          {/* Daily Overview */}
          <div className="daily-overview-section">
            <h2>Daily Overview</h2>
            <div className="daily-overview-grid">
              {dailyOverview.map((day, index) => (
                <div key={index} className="daily-item">
                  <div className="daily-date">{day.dateFormatted}</div>
                  <div className="daily-day-name">{formatDayName(day.date)}</div>
                  <div className="daily-mood-svg">
                    <img 
                      src={getMoodSvg(day.mostFrequentMood)} 
                      alt={day.mostFrequentMood}
                      className="daily-svg"
                    />
                  </div>
                  <div className="daily-stats">
                    <div className="daily-score">{day.averageMoodScore}</div>
                    <div className="daily-entries">{day.totalEntries} logs</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Mood Distribution */}
          <div className="mood-distribution-section">
            <div className="mood-distribution-container">
              <h2>Mood Distribution</h2>
              <div className="mood-distribution-grid">
                {Object.entries(moodDistribution).map(([moodKey, data]) => (
                  <div key={moodKey} className={`mood-distribution-item ${moodKey}`}>
                    <div className="mood-info">
                      <img 
                        src={getMoodSvg(moodKey)} 
                        alt={moodKey}
                        className="mood-svg-icon"
                      />
                      <span className="mood-label">I'm {moodKey}</span>
                    </div>
                    <div className="mood-stats">
                      <div className="mood-count">{data.count} logs</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="header-divider"></div>

          {/* Mood Check-in History */}
          <div className="mood-history-section">
            <h2>Mood Check-in History</h2>
            <div className="history-controls">
              <div className="search-container">
                <input 
                  type="text" 
                  placeholder="Search patients..." 
                  className="search-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="filter-controls">
                <select 
                  className="mood-checkin-filter-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="date_desc">Sort by: Date (Newest)</option>
                  <option value="date_asc">Sort by: Date (Oldest)</option>
                  <option value="mood">Sort by: Mood Rating</option>
                  <option value="average">Sort by: Average Rating</option>
                </select>
              </div>
              <button className="export-button" onClick={handleExportPDF}>
                <img 
                  src={pdfIcon} 
                  alt="PDF" 
                  className="export-icon" 
                /> 
                Export PDF
              </button>
            </div>

            <div className="mood-history-table">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Patient</th>
                    <th>Average</th>
                    <th>Total Logs</th>
                    <th>End-of-Day Mood Check-in Log</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredMoodHistory().length > 0 ? (
                    getFilteredMoodHistory().map((entry, index) => (
                      <tr key={index}>
                        <td>{new Date(entry.date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}</td>
                        <td>
                          <div className="patient-name-cell">
                            {entry.patientName}
                            {getRiskLevelIndicator(entry.sentimentSummary)}
                          </div>
                        </td>
                        <td>{entry.averageRating ? `${entry.averageRating.toFixed(1)}/5` : 'N/A'}</td>
                        <td>{entry.totalLogs} logs</td>
                        <td>
                          {entry.lastMood ? (
                            <span className={`mood-indicator ${entry.lastMood.moodKey}`}>
                              <img 
                                src={getMoodSvg(entry.lastMood.moodKey)} 
                                alt={entry.lastMood.moodKey}
                                className="mood-svg-small"
                              />
                              {entry.lastMood.moodLabel}
                            </span>
                          ) : (
                            <span className="mood-indicator okay">
                              <img 
                                src={getMoodSvg('okay')} 
                                alt="okay"
                                className="mood-svg-small"
                              />
                              No recent mood
                            </span>
                          )}
                        </td>
                        <td>
                          <button 
                            className="docaction-button view-mood-button"
                            onClick={() => handleViewMoodAnalysis(entry)}
                            title="View NLP Mood Analysis"
                          >
                            <svg className="button-icon" viewBox="0 0 24 24" fill="none" style={{width: '22px', height: '22px'}}>
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <circle cx="12" cy="12" r="3" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                        {patientMoodHistory.length === 0 
                          ? 'No mood check-in data available for your patients'
                          : 'No patients match your search criteria'
                        }
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </DoctorStatusGuard>
    </>
  );
};

export default PatientMoodCheckIns;